import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2, AlertCircle, ListTree } from 'lucide-react'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { useHasRedeemedKey } from '@/features/solution-browser/hooks/useActivationKey'
import { UnlockDialog } from '@/features/payments'
import { trackPYQBrowseStarted } from '@/shared/utils/analytics'
import { fbTrackViewContent } from '@/shared/utils/facebookPixel'
import { useMediaQuery } from '@/shared/hooks/useMediaQuery'
import type { HierarchyNodeResponse } from '@/features/syllabus/types'
import SyllabusUnitTree from '../components/SyllabusUnitTree'
import NodeQuestionBrowser from '../components/NodeQuestionBrowser'
import { useSubjectTree } from '../hooks/usePYQ'
import { SYLLABUS_SUBJECTS, SYLLABUS_DESKTOP_QUERY, toSubjectCode, findBrowsableNode } from '../utils/syllabusSubjects'

/**
 * Browse PYQs by syllabus: pick a subject, then a unit or topic.
 * - Desktop: tree, question list and reading pane side by side.
 * - Smaller widths: tree only; a node opens /question-bank/syllabus-browser.
 * State lives in the URL: ?subject=<subject node code>&node=<node code>&q=<question number>
 */
export default function PYQBySyllabusPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const isDesktop = useMediaQuery(SYLLABUS_DESKTOP_QUERY)
  const userId = useAuthStore((s) => s.userId) ?? undefined
  const access = useHasRedeemedKey(userId)
  // Free user = no REDEEMED activation key (no test series bought)
  const isFreeUser = !access.hasRedeemedKey
  const [showUnlock, setShowUnlock] = useState(false)

  const subjectCode = toSubjectCode(searchParams.get('subject'))
  const treeQuery = useSubjectTree(subjectCode)
  const subject = treeQuery.data
  const isLoading = treeQuery.isLoading || access.isLoading
  const isError = treeQuery.isError || access.isError
  const refetch = () => {
    if (treeQuery.isError) treeQuery.refetch()
    if (access.isError) access.refetch()
  }
  const selectedNode = isFreeUser ? undefined : findBrowsableNode(subject, searchParams.get('node'))

  // Track ViewContent for FB Pixel
  useEffect(() => {
    fbTrackViewContent('pyq_by_syllabus', undefined, 'Browse PYQ by Syllabus')
  }, [])

  const handleSelect = (node: HierarchyNodeResponse) => {
    if (isFreeUser) {
      setShowUnlock(true)
      return
    }
    trackPYQBrowseStarted('syllabus')
    const params = new URLSearchParams({ subject: subjectCode, node: node.nodeCode })
    if (isDesktop) {
      setSearchParams(params)
    } else {
      navigate(`/question-bank/syllabus-browser?${params}`)
    }
  }

  const tree = isLoading ? (
    <div className="flex justify-center py-16">
      <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
    </div>
  ) : isError || !subject ? (
    <div className="flex flex-col items-center gap-2 py-12 text-center">
      <AlertCircle className="h-8 w-8 text-red-500" />
      <p className="text-sm text-gray-700">Couldn't load the syllabus.</p>
      <button onClick={() => refetch()} className="text-sm font-medium text-blue-600 underline">
        Try again
      </button>
    </div>
  ) : (
    <SyllabusUnitTree
      key={subjectCode}
      subject={subject}
      selectedNodeCode={selectedNode?.nodeCode ?? null}
      onSelect={handleSelect}
      locked={isFreeUser}
    />
  )

  return (
    // Desktop: fill the viewport below the app header (4rem) minus MainLayout's padding (3rem)
    <div className="flex flex-col xl:h-[calc(100vh-7rem)]">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Browse PYQ by Syllabus</h1>
          <p className="mt-1 text-sm text-gray-600 sm:text-base">
            Pick a unit or topic to see every previous year question on it.
          </p>
        </div>

        <div role="tablist" aria-label="Subject" className="inline-flex self-start rounded-lg bg-gray-100 p-1">
          {SYLLABUS_SUBJECTS.map((s) => (
            <button
              key={s.nodeCode}
              role="tab"
              aria-selected={s.nodeCode === subjectCode}
              onClick={() => setSearchParams({ subject: s.nodeCode })}
              className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                s.nodeCode === subjectCode ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {isDesktop ? (
        <div className="flex min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <aside className="w-64 flex-shrink-0 overflow-y-auto border-r border-gray-200 p-2" aria-label="Syllabus">
            {tree}
          </aside>
          <div className="min-w-0 flex-1">
            {selectedNode ? (
              <NodeQuestionBrowser
                key={selectedNode.nodeCode}
                nodeCode={selectedNode.nodeCode}
                nodeName={selectedNode.nodeName}
                isWide
                header={
                  <div className="flex-shrink-0 border-b border-gray-200 px-4 py-3">
                    <h2 className="truncate font-semibold text-gray-900">{selectedNode.nodeName}</h2>
                  </div>
                }
              />
            ) : (
              <div className="flex h-full flex-col items-center justify-center p-6 text-center">
                <ListTree className="mb-3 h-10 w-10 text-gray-400" />
                <h3 className="font-medium text-gray-700">Select a unit or topic</h3>
                <p className="mt-1 text-sm text-gray-500">Its previous year questions will appear here.</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white p-2 shadow-sm">{tree}</div>
      )}

      <UnlockDialog
        isOpen={showUnlock}
        onClose={() => setShowUnlock(false)}
        title="Unlock syllabus-wise PYQs"
        description="Practise previous year questions for every unit and topic."
        source="pyq_by_syllabus"
      />
    </div>
  )
}
